import {
    useRef,
    useState,
    useEffect,
    useCallback,
    forwardRef,
    useImperativeHandle,
} from "react";
import {
    PlayIcon,
    PauseIcon,
    Volume2Icon,
    VolumeXIcon,
    DownloadIcon,
    Loader2Icon,
    FileAudioIcon,
} from "lucide-react";
import type { BeFeedbackItem } from "@type/training";
import {
    getSafeRecordingUrl,
} from "@util/training-feedback-adapter";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";

export interface RecordingPlayerHandle {
    seekTo: (timestamp: string) => void;
    playFrom: (timestamp: string) => void;
}

interface RecordingPlayerProps {
    recordingUrl: string | null;
    feedbackItems?: BeFeedbackItem[];
    onTimestampClick?: (timestamp: string) => void;
    /**
     * When true, the parent session is still being scored — render a
     * "waiting" empty state instead of the player chrome (which would
     * otherwise sit at 0:00 / 0:00 looking broken until scoring finishes).
     */
    isProcessing?: boolean;
}

// Returns seconds for HH:MM:SS / MM:SS / SS strings, or `null` for
// empty / malformed input. The previous implementation returned 0 in those
// cases, which silently seeked the player to the start whenever the BE
// shipped a missing or unparseable timestamp — a footgun masquerading as
// a working seek.
function parseTimestamp(ts: string): number | null {
    if (!ts || typeof ts !== "string") return null;
    const trimmed = ts.trim();
    if (!trimmed) return null;
    const parts = trimmed.split(":").map((p) => parseInt(p, 10));
    if (parts.some(Number.isNaN)) return null;
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    if (parts.length === 1) return parts[0];
    return null;
}

function formatTime(sec: number): string {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
}

const SPEED_OPTIONS = [0.75, 1, 1.25, 1.5, 2] as const;

// Timeline-marker colour per feedback category. Mirrors CoachingMomentCard's
// DOT_COLORS so a Mistake reads red, an Inaccuracy amber, and positives green —
// the previous binary (positive ? emerald : amber) collapsed Inaccuracy and
// Mistake into the same amber, hiding the red markers entirely.
const MARKER_COLORS: Record<BeFeedbackItem["category"], string> = {
    Brilliant: "bg-purple-500",
    Excellent: "bg-green-500",
    Good: "bg-emerald-500",
    Inaccuracy: "bg-amber-500",
    Mistake: "bg-red-500",
};

export const RecordingPlayer = forwardRef<
    RecordingPlayerHandle,
    RecordingPlayerProps
>(function RecordingPlayer({ recordingUrl, feedbackItems = [], onTimestampClick, isProcessing = false }, ref) {
    const audioRef = useRef<HTMLAudioElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [speed, setSpeed] = useState(1);
    const [isDragging, setIsDragging] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [hoveredDot, setHoveredDot] = useState<BeFeedbackItem | null>(null);
    // Audio bytes loaded as a blob and re-served via object URL so the
    // `<audio>` element treats it as fully seekable. The backend audio
    // endpoint currently responds 200 OK without Accept-Ranges / 206
    // support, which makes browsers report `audio.seekable = [[0,0]]`
    // and silently clamp every `currentTime` write to 0 — the "click
    // resets to start" bug. The blob trick sidesteps that until BE
    // adds proper range support.
    const [objectUrl, setObjectUrl] = useState<string | null>(null);
    const [audioLoading, setAudioLoading] = useState(false);

    const safeUrl = getSafeRecordingUrl(recordingUrl);

    const applySeek = useCallback((timestamp: string, autoplay: boolean) => {
        const audio = audioRef.current;
        if (!audio) return;
        const seconds = parseTimestamp(timestamp);
        // Invalid / empty timestamp → do NOT touch currentTime. This is the
        // root of the "click green dot → jumps to 0" bug: the BE occasionally
        // sends an empty timestamp string, which used to silently seek to 0.
        if (seconds === null) return;
        const apply = () => {
            // Clamp to known duration when available — Chrome silently snaps
            // out-of-range currentTime to 0, which is the bug we just fixed.
            const safe = audio.duration && Number.isFinite(audio.duration)
                ? Math.min(seconds, Math.max(0, audio.duration - 0.05))
                : Math.max(0, seconds);
            audio.currentTime = safe;
            setCurrentTime(safe);
            if (autoplay) {
                audio.play().then(() => setIsPlaying(true)).catch(() => {
                    /* user gesture missing / unsupported source — leave paused */
                });
            }
        };
        if (audio.readyState >= 1 /* HAVE_METADATA */) {
            apply();
        } else {
            // Defer until metadata is known so we don't write currentTime
            // before the element knows its duration (which gets reset on load).
            const onReady = () => {
                audio.removeEventListener("loadedmetadata", onReady);
                apply();
            };
            audio.addEventListener("loadedmetadata", onReady);
        }
    }, []);

    useImperativeHandle(ref, () => ({
        seekTo(timestamp: string) {
            applySeek(timestamp, false);
        },
        playFrom(timestamp: string) {
            applySeek(timestamp, true);
        },
    }));

    useEffect(() => {
        const audio = audioRef.current;
        // `audioRef` is null during the isProcessing empty-state render
        // (component returns early before the <audio> element mounts). Once
        // isProcessing flips false or objectUrl arrives, the element mounts
        // and this effect must re-run to attach listeners — hence depending
        // on `objectUrl` and `isProcessing`, not just `safeUrl`. Otherwise
        // `durationchange` from the freshly-loaded blob is missed and React
        // `duration` state stays at 0 (the "stuck at 0:00 / 0:00" bug).
        if (!audio) return;

        const onTimeUpdate = () => setCurrentTime(audio.currentTime);
        const onDurationChange = () => setDuration(audio.duration || 0);
        const onEnded = () => setIsPlaying(false);
        const onError = () => setLoadError(true);

        audio.addEventListener("timeupdate", onTimeUpdate);
        audio.addEventListener("durationchange", onDurationChange);
        audio.addEventListener("ended", onEnded);
        audio.addEventListener("error", onError);

        return () => {
            audio.removeEventListener("timeupdate", onTimeUpdate);
            audio.removeEventListener("durationchange", onDurationChange);
            audio.removeEventListener("ended", onEnded);
            audio.removeEventListener("error", onError);
        };
    }, [safeUrl, objectUrl, isProcessing]);

    // Fetch the recording as a blob and re-serve via objectURL so the audio
    // element treats it as fully seekable. See objectUrl state comment for
    // the full rationale (BE doesn't support HTTP Range requests yet).
    //
    // Retries every 1s while the endpoint isn't ready so the player auto-
    // appears the moment processing finishes — no manual reload required.
    useEffect(() => {
        if (!safeUrl) {
            setObjectUrl(null);
            return;
        }
        // Skip the fetch while the parent session is still being scored.
        // The audio endpoint may return placeholder/partial bytes mid-pipeline
        // that, if cached into objectUrl, leave a broken player (0:00 / 0:00,
        // refuses to play) even after scoring finishes. Effect re-runs when
        // isProcessing flips to false and grabs the final file fresh.
        if (isProcessing) {
            setObjectUrl(null);
            setAudioLoading(false);
            setLoadError(false);
            return;
        }
        let cancelled = false;
        let createdUrl: string | null = null;
        let retryTimer: number | null = null;
        // Cap retries so a permanently broken endpoint doesn't pound the
        // server forever. 5 minutes at 1 req/s = 300 attempts.
        const MAX_ATTEMPTS = 300;
        const RETRY_DELAY_MS = 1000;
        let attempt = 0;

        // Probe a candidate object URL with a detached Audio element — only
        // commit it if the file actually has a positive duration. Without
        // this, a partial/placeholder response from BE during the post-
        // scoring window can show as a "valid" blob but produce a player
        // that's stuck at 0:00 / 0:00 and refuses to play.
        const probeDuration = (url: string): Promise<number> =>
            new Promise((resolve, reject) => {
                const probe = new Audio();
                let settled = false;
                const cleanup = () => {
                    settled = true;
                    probe.removeAttribute("src");
                    probe.load();
                };
                const timer = window.setTimeout(() => {
                    if (settled) return;
                    cleanup();
                    reject(new Error("Audio metadata timed out"));
                }, 6000);
                probe.addEventListener("loadedmetadata", () => {
                    if (settled) return;
                    window.clearTimeout(timer);
                    const dur = probe.duration;
                    cleanup();
                    if (Number.isFinite(dur) && dur > 0) resolve(dur);
                    else reject(new Error(`Audio duration invalid: ${dur}`));
                }, { once: true });
                probe.addEventListener("error", () => {
                    if (settled) return;
                    window.clearTimeout(timer);
                    cleanup();
                    reject(new Error("Audio failed to decode"));
                }, { once: true });
                probe.preload = "metadata";
                probe.src = url;
            });

        const tryFetch = async () => {
            if (cancelled) return;
            attempt += 1;
            // Only the very first attempt shows the "Loading…" spinner; later
            // attempts are silent so the pending banner doesn't flicker.
            if (attempt === 1) setAudioLoading(true);
            let candidateUrl: string | null = null;
            try {
                const res = await fetch(safeUrl);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const blob = await res.blob();
                if (cancelled) return;
                if (blob.size === 0) throw new Error("Empty audio blob");
                candidateUrl = URL.createObjectURL(blob);
                // Validate that the blob actually plays back with a real
                // duration. If not, treat as not-ready and keep polling.
                const probedDuration = await probeDuration(candidateUrl);
                if (cancelled) {
                    URL.revokeObjectURL(candidateUrl);
                    return;
                }
                createdUrl = candidateUrl;
                setObjectUrl(candidateUrl);
                // Seed the duration state directly from the probe so the UI
                // shows the correct time immediately, even if the main audio
                // element's `durationchange` event fires later (or not at all
                // on some browsers when the blob is already in cache).
                setDuration(probedDuration);
                setAudioLoading(false);
                setLoadError(false);
            } catch (err: unknown) {
                // Discard the candidate so we don't leak the URL on retries
                // — only the LAST successful candidate is kept in `createdUrl`
                // for cleanup on unmount.
                if (candidateUrl) URL.revokeObjectURL(candidateUrl);
                if (cancelled) return;
                setAudioLoading(false);
                // Surface the pending state so the user sees the "still
                // processing" banner while we keep polling in the background.
                setLoadError(true);
                if (attempt >= MAX_ATTEMPTS) {
                    const msg = err instanceof Error ? err.message : String(err);
                    catchErrorLog(`[training] recording fetch gave up after ${attempt} attempts: ${msg}`);
                    return;
                }
                retryTimer = window.setTimeout(tryFetch, RETRY_DELAY_MS);
            }
        };

        tryFetch();

        return () => {
            cancelled = true;
            if (retryTimer !== null) window.clearTimeout(retryTimer);
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [safeUrl, isProcessing]);

    const togglePlay = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;
        if (isPlaying) {
            audio.pause();
        } else {
            audio.play();
        }
        setIsPlaying(!isPlaying);
    }, [isPlaying]);

    const toggleMute = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.muted = !isMuted;
        setIsMuted(!isMuted);
    }, [isMuted]);

    const setPlaybackSpeed = useCallback((s: number) => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.playbackRate = s;
        setSpeed(s);
    }, []);

    const seekToPosition = useCallback(
        (clientX: number) => {
            const track = trackRef.current;
            const audio = audioRef.current;
            if (!track || !audio) return;
            // Prefer the audio element's known duration over our React-state
            // copy; state lags an extra render behind durationchange.
            const dur = Number.isFinite(audio.duration) && audio.duration > 0
                ? audio.duration
                : duration;
            if (!dur) return;
            const rect = track.getBoundingClientRect();
            const ratio = Math.max(
                0,
                Math.min(1, (clientX - rect.left) / rect.width),
            );
            // Clamp away from the absolute end — some browsers treat
            // currentTime === duration as "past end" and snap to 0,
            // which is exactly the "clicks reset to start" bug.
            const target = ratio * dur;
            const safe = Math.min(target, Math.max(0, dur - 0.05));
            audio.currentTime = safe;
            setCurrentTime(safe);
        },
        [duration],
    );

    const handleTrackMouseDown = useCallback(
        (e: React.MouseEvent) => {
            setIsDragging(true);
            seekToPosition(e.clientX);
        },
        [seekToPosition],
    );

    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e: MouseEvent) => seekToPosition(e.clientX);
        const handleMouseUp = () => setIsDragging(false);

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging, seekToPosition]);

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    // Session still being scored — show a friendly "waiting" empty state
    // that matches Transcript / coaching-tab empty states elsewhere on the
    // page. Without this, the player chrome would mount at 0:00 / 0:00
    // and look broken until the scoring pipeline drops the recording.
    if (isProcessing) {
        return (
            <div className="flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-white py-12 px-4 text-center">
                <div className="bg-gray-100 p-4 rounded-full mb-4">
                    <FileAudioIcon className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                    Recording is not ready yet.
                </h3>
                <p className="text-xs text-gray-500 max-w-xs">
                    Your roleplay audio will appear here once the scoring pipeline finishes processing.
                </p>
            </div>
        );
    }

    if (!safeUrl) {
        return (
            <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-500">
                Recording not available.
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="flex items-center justify-center rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-sm text-amber-700">
                Audio is still processing — it'll appear here once it's ready, no need to reload.
            </div>
        );
    }

    if (audioLoading && !objectUrl) {
        return (
            <div className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-500">
                <Loader2Icon className="h-4 w-4 animate-spin" />
                Loading recording…
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4">
            <audio
                ref={audioRef}
                // Prefer the blob `objectUrl` (fully seekable) and fall back
                // to the direct URL if the blob fetch failed. Blob URLs are
                // same-origin so no crossOrigin attribute needed.
                src={objectUrl ?? safeUrl}
                preload="auto"
            />

            {/* Controls row */}
            <div className="flex items-center gap-3">
                {/* Play / Pause */}
                <button
                    type="button"
                    onClick={togglePlay}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                    {isPlaying ? (
                        <PauseIcon className="h-4 w-4" />
                    ) : (
                        <PlayIcon className="h-4 w-4 ml-0.5" />
                    )}
                </button>

                {/* Scrub bar */}
                <div className="flex flex-1 flex-col gap-1">
                    <div
                        ref={trackRef}
                        className="relative h-2 cursor-pointer rounded-full bg-gray-200"
                        onMouseDown={handleTrackMouseDown}
                    >
                        {/* Progress fill */}
                        <div
                            className="absolute inset-y-0 left-0 rounded-full bg-blue-500"
                            style={{ width: `${progress}%` }}
                        />

                        {/* Scrub handle — slightly larger + scale affordance so
                            it reads as draggable. Drag itself is driven by the
                            parent track's mousedown handler (click bubbles up). */}
                        <div
                            className={cn(
                                "absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-5 w-5 rounded-full border-2 border-blue-600 bg-white shadow-sm transition-transform",
                                isDragging ? "scale-110 cursor-grabbing" : "hover:scale-105 cursor-grab",
                            )}
                            style={{ left: `${progress}%` }}
                        />

                        {/* Feedback dots */}
                        {duration > 0 &&
                            feedbackItems.map((item, idx) => {
                                // Skip dots whose timestamp the BE didn't ship
                                // (or shipped malformed) — clicking them would
                                // be a confusing no-op, so don't render at all.
                                const seconds = parseTimestamp(item.timestamp);
                                if (seconds === null) return null;
                                // Also skip out-of-range timestamps; they'd
                                // render outside the track and (now that the
                                // parent wrapper isn't clipping for the
                                // tooltip's sake) bleed into the layout.
                                if (seconds < 0 || seconds > duration) return null;
                                const pos = (seconds / duration) * 100;
                                return (
                                    <div
                                        key={idx}
                                        // Hit area = the visible dot only (12×12).
                                        // Padding here would steal click pixels
                                        // away from the surrounding track, so a
                                        // user trying to scrub to a custom
                                        // timestamp near a dot would always
                                        // snap to the dot instead.
                                        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 h-3 w-3 cursor-pointer"
                                        style={{ left: `${pos}%` }}
                                        onMouseEnter={() =>
                                            setHoveredDot(item)
                                        }
                                        onMouseLeave={() =>
                                            setHoveredDot(null)
                                        }
                                        // Block the parent track's mousedown so
                                        // the dot's click handler is the only
                                        // seek action when the user clicks the
                                        // dot itself.
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onTimestampClick?.(item.timestamp);
                                        }}
                                    >
                                        <div
                                            className={cn(
                                                "h-3 w-3 rounded-full border-2 border-white",
                                                MARKER_COLORS[item.category] ?? "bg-amber-500",
                                            )}
                                        />
                                        {hoveredDot === item && (
                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 z-50 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white shadow-lg">
                                                {item.title}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                </div>

                {/* Time display */}
                <span className="shrink-0 text-xs tabular-nums text-gray-500">
                    {formatTime(currentTime)} / {formatTime(duration)}
                </span>

                {/* Volume toggle */}
                <button
                    type="button"
                    onClick={toggleMute}
                    className="flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:text-gray-700 transition-colors"
                >
                    {isMuted ? (
                        <VolumeXIcon className="h-4 w-4" />
                    ) : (
                        <Volume2Icon className="h-4 w-4" />
                    )}
                </button>

                {/* Download */}
                <a
                    href={safeUrl}
                    download
                    className="flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:text-gray-700 transition-colors"
                >
                    <DownloadIcon className="h-4 w-4" />
                </a>
            </div>

            {/* Speed selector */}
            <div className="flex items-center gap-1">
                <span className="text-xs text-gray-400 mr-1">Speed</span>
                {SPEED_OPTIONS.map((s) => (
                    <button
                        key={s}
                        type="button"
                        onClick={() => setPlaybackSpeed(s)}
                        className={cn(
                            "rounded px-2 py-0.5 text-xs font-medium transition-colors",
                            speed === s
                                ? "bg-blue-100 text-blue-700"
                                : "text-gray-500 hover:bg-gray-100",
                        )}
                    >
                        {s}x
                    </button>
                ))}
            </div>
        </div>
    );
});
